// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@conklin/App/Store/Reducers";
import LazyPicture from "@conklin/MobiusOverrides/LazyImage/LazyPicture";
import Link from "@conklin/MobiusOverrides/Link/Link";
import { xParserOptions } from "@conklin/XDL/Utilities";
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    focalPoint = "focalPoint",
    backgroundColor = "backgroundColor",
    heading = "heading",
    minimumHeight = "minimumHeight",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    bannerWidth = "bannerWidth",
    imageOverlay = "imageOverlay",
    partialOverlay = "partialOverlay",
    partialOverlayPositioning = "partialOverlayPositioning",
    disableButton = "disableButton",
    contentPadding = "contentPadding",
    centerTextVertically = "centerTextVertically",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
    // XNG-Changes
    customHeight = "customHeight",
    contentWidth = "contentWidth",
    imageMobile = "imageMobile",
    imageTablet = "imageTablet",
    imageAltText = "imageAltText",
    buttonLight = "buttonLight",
    bannerClickable = "bannerClickable",
    bannerClickableLink = "bannerClickableLink",
    openInNewTab = "openInNewTab",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.minimumHeight]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport" | "customValue";
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary" | "link";
        [fields.imageOverlay]: string;
        [fields.partialOverlay]: boolean;
        [fields.partialOverlayPositioning]: "top" | "middle" | "bottom";
        [fields.disableButton]: boolean;
        [fields.contentPadding]: string;
        [fields.centerTextVertically]: boolean;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.customHeight]: number;
        [fields.contentWidth]: string;
        [fields.imageTablet]: string;
        [fields.imageMobile]: string;
        [fields.imageAltText]: string;
        [fields.buttonLight]: boolean;
        [fields.bannerClickable]: boolean;
        [fields.bannerClickableLink]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    /**
     * @deprecated Use the `bannerButton` property instead.
     */
    slideCenteringWrapperStyles?: InjectableCss;
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
    // XNG-Changes
    pictureWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    subHeading?: TypographyPresentationProps;
    bannerClickable?: ClickablePresentationProps;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: white;
            text-align: center;
        `,
    },
};

export const Banner: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { url, title } = useGetLink(fields.buttonLink);
    const { url: bannerUrl, title: bannerTitle } = useGetLink(fields.bannerClickableLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    // XNG-Changes
    const backgroundStyles = fields.background === "color" && `background-color: ${fields.backgroundColor};`;

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    let minimumHeightStyles;
    switch (fields.minimumHeight) {
        case "1/4 viewport":
            minimumHeightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            minimumHeightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            minimumHeightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            minimumHeightStyles = "min-height: 100vh;";
            break;
        case "customValue":
            minimumHeightStyles = `min-height: ${fields.customHeight}px;`;
            break;
    }

    let overlayPositioningStyles;
    if (!fields.partialOverlay) {
        overlayPositioningStyles = "align-items: stretch;";
    } else if (fields.partialOverlayPositioning === "top") {
        overlayPositioningStyles = "align-items: flex-start;";
    } else if (fields.partialOverlayPositioning === "middle") {
        overlayPositioningStyles = "align-items: center;";
    } else if (fields.partialOverlayPositioning === "bottom") {
        overlayPositioningStyles = "align-items: flex-end;";
    }
    const styles = useMergeStyles("banner", bannerStyles, extendedStyles);

    let fontSizeStyles;
    if (fields.responsiveFontSizes || fields.customFontSizes) {
        fontSizeStyles = responsiveStyleRules(fields.responsiveFontSizes, fields.customFontSizes ? fields : undefined);
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
            ${minimumHeightStyles}
            ${overlayPositioningStyles}
            ${fontSizeStyles}
        `,
    };

    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
            padding: ${fields.contentPadding};
            width: ${fields.contentWidth};
            ${fields.centerTextVertically &&
            `
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;`}
        `,
    };

    const isTablet = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    const BannerContent = () => (
        <StyledWrapper className="banner-wrapper" {...wrapperStyles}>
            {/* XNG-Changes use lazy picture for multi img srcs */}
            {fields.background === "image" && (
                <StyledWrapper {...styles.pictureWrapper}>
                    <LazyPicture
                        altText={fields.imageAltText || translate("Banner-image")}
                        largeImageSrc={fields.image}
                        mediumImageSrc={fields.imageTablet}
                        smallImageSrc={fields.imageMobile}
                    />
                </StyledWrapper>
            )}
            {(fields.heading || fields.subheading) && (
                <StyledWrapper className="banner-overlay-wrapper" {...overlayWrapperStyles}>
                    <StyledWrapper className="banner-center-wrapper" {...styles.contentWrapper}>
                        <Typography className="banner-heading" {...styles.heading}>
                            {parse(fields.heading, xParserOptions)}
                        </Typography>
                        <Typography className="banner-subheading" {...styles.subHeading}>
                            {parse(fields.subheading, xParserOptions)}
                        </Typography>
                        {!fields.disableButton &&
                            url &&
                            (fields.variant !== "link" ? (
                                <Button
                                    {...styles.bannerButton}
                                    variant={fields.variant}
                                    onClick={e => {
                                        e.preventDefault();
                                        onClick();
                                    }}
                                    sizeVariant="medium"
                                    light={isTablet ? false : fields.buttonLight}
                                >
                                    {fields.buttonLabel || title || url}
                                </Button>
                            ) : (
                                <Link
                                    onClick={e => {
                                        e.preventDefault();
                                        onClick();
                                    }}
                                    aria-label={fields.buttonLabel}
                                    {...styles.bannerLink}
                                >
                                    {fields.buttonLabel || title || url}
                                </Link>
                            ))}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );

    // XNG-Changes: change structure + add clickable to banner
    return (
        <StyledWrapper {...customCssWrapper}>
            {fields.bannerClickable && bannerUrl ? (
                <Clickable href={bannerUrl} target={fields.openInNewTab ? "_blank" : ""}>
                    <BannerContent />
                </Clickable>
            ) : (
                <BannerContent />
            )}
        </StyledWrapper>
    );
};

const defaultCustomCss = `.banner-wrapper {
}

.banner-overlay-wrapper {
}

.banner-center-wrapper {
}

.banner-heading {
}

.banner-subheading {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const banner: WidgetModule = {
    component: Banner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.image,
                displayName: "Desktop Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            // << XNG-Changes >>
            {
                fieldType: "Contextual",
                name: fields.imageTablet,
                editorTemplate: "ImagePickerField",
                displayName: "Tablet Image",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.imageMobile,
                editorTemplate: "ImagePickerField",
                displayName: "Mobile Image",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            // << XNG-Changes >>
            {
                fieldType: "Contextual",
                name: fields.imageOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            // XNG-Changes imageAltText
            {
                fieldType: "Contextual",
                name: fields.imageAltText,
                editorTemplate: "TextField",
                displayName: "Image Alt Text",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.partialOverlay,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.partialOverlayPositioning,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Middle", value: "middle" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                hideEmptyOption: true,
                defaultValue: "bottom",
                isVisible: widget => widget.fields.partialOverlay,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            // XNG-Changes: add option to make banner clickable
            {
                fieldType: "Contextual",
                name: fields.bannerClickable,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.bannerClickableLink,
                displayName: "Banner Clickable Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                isVisible: widget => widget.fields.bannerClickable === true,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isVisible: widget => widget.fields.bannerClickable === true,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {},
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                    { displayName: "Link", value: "link" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.buttonLight,
                displayName: "Button Light Mode",
                editorTemplate: "CheckboxField",

                defaultValue: false,
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.minimumHeight,
                displayName: "Minimum Banner Height",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                    // XNG-Changes
                    { displayName: "Custom Value", value: "customValue" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: basicTab,
            },
            // XNG-Changes add customHeight
            {
                fieldType: "Contextual",
                name: fields.customHeight,
                editorTemplate: "IntegerField",
                defaultValue: 333,
                tab: basicTab,
                isVisible: (item: HasFields) => item.fields.minimumHeight === "customValue",
            },
            {
                fieldType: "Contextual",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                tab: settingsTab,
                defaultValue: false,
            },
            // XNG-Changes change contentPadding from number to padding value string
            {
                fieldType: "Contextual",
                name: "contentPadding",
                editorTemplate: "TextField",
                defaultValue: "0",
                placeholder: "Enter: 5px, 5px auto, ...etc",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
            },
            // XNG-Changes add contentWidth
            {
                fieldType: "Contextual",
                name: "contentWidth",
                editorTemplate: "TextField",
                defaultValue: "auto",
                placeholder: "Enter: 5px, auto, ...etc",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("width", value) ? null : `Invalid Input`;
                },
            },
            {
                fieldType: "Contextual",
                name: "centerTextVertically",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default banner;
